import { BigNumber } from "@yjsgames/bignumber";
import { STAT_UPGRADE_DEFS, type StatUpgradeDef } from "../data/balance.js";
import { evalStatExpression } from "./evalStatExpression.js";

/** statId → 현재 강화 레벨. 미등록 statId는 레벨 0으로 취급. */
export type StatUpgradeLevels = Record<string, number>;

/**
 * statId별 영구 강화 — 순수 로직 (Phaser 비의존, 단위 테스트 대상).
 * statId별 레벨을 보관하고 비용 누진(costExpr)·효과(expression 평가)를 계산한다.
 * 재화 차감은 호출자(GameScene)가 수행하고, 본 컨트롤러는 maxLevel 검증 + 레벨 증가 + 효과 환산만 담당한다.
 *
 * defs 주입형 (utility-class-generalize) — 기본값 STAT_UPGRADE_DEFS(골드 소모 스탯). 환생 강화는
 * REBIRTH_UPGRADE_DEFS를 주입해 동일 로직을 재사용한다(재화만 다름: 골드 vs 환생 포인트, 차감은 호출자 책임).
 * 효과 해석은 도메인 메서드(attack=기본 피해 가산, fireRate=발사 간격 감소 ms, goldGain=골드 배수)
 * 또는 effectOf(statId)(pointBonus 등 도메인 메서드 없는 stat)로 호출자가 결정한다.
 */
export class StatUpgradeController {
  private readonly _levels: StatUpgradeLevels = {};

  constructor(
    initial?: StatUpgradeLevels,
    private readonly defs: Record<string, StatUpgradeDef> = STAT_UPGRADE_DEFS,
  ) {
    if (initial) {
      for (const [id, lv] of Object.entries(initial)) {
        // 정의에 없는 statId는 폐기(예: 폐기된 projectileSpeed 레벨 자동 무시), 음수/소수는 방어적으로 정규화.
        if (id in this.defs) this._levels[id] = Math.max(0, Math.floor(lv));
      }
    }
  }

  /** statId의 현재 레벨 (미등록/미강화 = 0). */
  level(statId: string): number {
    return this._levels[statId] ?? 0;
  }

  /** statId의 만렙 여부. 미등록 statId는 true(강화 불가). */
  isMaxed(statId: string): boolean {
    const def = this.defs[statId];
    if (!def) return true;
    return this.level(statId) >= def.maxLevel;
  }

  /**
   * statId의 특정 레벨에서 "다음 1레벨" 강화 비용 = round(evalStatExpression(costExpr, baseCost, level)).
   * level을 파라미터로 받아 멀티 구매(multiBuy.totalCostForCount)의 레벨별 누적 합산에 재사용된다.
   * 미등록 statId는 ZERO. (호출자가 통화 타입에 맞게 형변환 — 예: number 재화는 cost().toNumber()로 비교.)
   */
  unitCostAt(statId: string, level: number): BigNumber {
    const def = this.defs[statId];
    if (!def) return BigNumber.ZERO;
    return BigNumber.fromNumber(Math.round(evalStatExpression(def.costExpr, def.baseCost, level)));
  }

  /**
   * 다음 레벨 강화 비용(BigNumber) = unitCostAt(현재 레벨).
   * 만렙 차단은 isMaxed가 담당 (호출부가 isMaxed 가드 — cost는 다음 레벨 명목값).
   */
  cost(statId: string): BigNumber {
    return this.unitCostAt(statId, this.level(statId));
  }

  /** statId의 만렙 레벨 (미등록 = 0). 멀티 구매 잔여 레벨 클램프용. */
  maxLevelOf(statId: string): number {
    return this.defs[statId]?.maxLevel ?? 0;
  }

  /** 레벨 +1 (만렙/미등록이면 무변경 + false). 재화 차감은 호출자 책임. */
  upgrade(statId: string): boolean {
    if (this.isMaxed(statId)) return false;
    this._levels[statId] = this.level(statId) + 1;
    return true;
  }

  /**
   * count 레벨 일괄 강화 (maxLevel 클램프). 실제 증가한 레벨 수를 반환한다.
   * 총 재화 차감은 호출자(GameScene)가 multiBuy.resolvePurchase로 총비용을 계산해 차감한다.
   */
  upgradeMany(statId: string, count: number): number {
    const cur = this.level(statId);
    const applied = Math.max(0, Math.min(Math.floor(count), this.maxLevelOf(statId) - cur));
    if (applied > 0) this._levels[statId] = cur + applied;
    return applied;
  }

  /** attack 강화로 인한 기본 피해 가산값 (정수). */
  attackBonus(): number {
    return this.effect("attack");
  }

  /** fireRate 강화로 인한 발사 간격 감소량(ms). */
  fireRateReductionMs(): number {
    return this.effect("fireRate");
  }

  /** goldGain 강화 반영 골드 획득 배수 (기본 1.0). */
  goldMultiplier(): number {
    return 1 + this.effect("goldGain");
  }

  /** critChance 강화로 인한 누적 치명타 확률 가산(0~1, 미강화 0). 발사체 spec.critChance에 가산(composeCritChance). */
  critChanceBonus(): number {
    return this.effect("critChance");
  }

  /** critDamage 강화로 인한 누적 치명타 데미지 배수 가산(미강화 0). 기본 배수(2)에 가산(composeCritMultiplier). */
  critDamageBonus(): number {
    return this.effect("critDamage");
  }

  /**
   * statId의 누적 효과값 = expression({base},{level}) 평가 (미등록 = 0).
   * 도메인 메서드(attackBonus/fireRateReductionMs/goldMultiplier)가 없는 stat(환생 pointBonus 등) 직접 조회용.
   */
  effectOf(statId: string): number {
    return this.effectAt(statId, this.level(statId));
  }

  /**
   * statId의 특정 (가상) 레벨에서의 효과값 = expression({base}, {level}) 평가 (미등록 = 0).
   * 현재 레벨 효과는 effectOf/effect가 위임하고, UI의 "다음 레벨 예상값"은 가상 레벨(현재+배수 count)을
   * 직접 넣어 미리 계산한다 (레벨을 실제로 올리지 않고 표시값만 산정).
   */
  effectAt(statId: string, level: number): number {
    const def = this.defs[statId];
    if (!def) return 0;
    return evalStatExpression(def.expression, def.base, level);
  }

  /** statId의 누적 효과 = expression({base}, {level}) 평가값 (미등록 = 0). */
  private effect(statId: string): number {
    return this.effectAt(statId, this.level(statId));
  }

  /** 모든 강화 레벨 초기화 — 미강화(새 게임) 상태로. 환생 시 골드 스탯 강화 리셋에 사용. */
  resetAll(): void {
    for (const id of Object.keys(this._levels)) delete this._levels[id];
  }

  toState(): StatUpgradeLevels {
    return { ...this._levels };
  }
}
